// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

// =================================================================
// 1. DEFINISI CONTEXT DAN HOOK
// =================================================================
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// =================================================================
// 2. AUTHENTICATION PROVIDER COMPONENT
// =================================================================
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Memproses token SSO.
   * Modifikasi: Mengirim token ke backend untuk divalidasi ke LKPP 
   * sebelum mengizinkan user masuk ke aplikasi.
   */
  const exchangeToken = async (nonceToken) => {
    setLoading(true);
    setError(null);

    try {
      console.log('[AuthContext] Memproses SSO Token (Nonce):', nonceToken);

      // 1. Buka (Decode) Token Internal untuk mendapatkan data user
      const payloadPart = nonceToken.split('.')[1];
      if (!payloadPart) {
        throw new Error("Token tidak valid (format rusak)");
      }

      const decodedUserData = JSON.parse(atob(payloadPart));
      const tokenLKPPAsli = decodedUserData.lkppToken;

      if (!tokenLKPPAsli) {
        throw new Error("Sistem menolak: Token LKPP asli tidak ditemukan di dalam sesi.");
      }

      // 2. TIDAK perlu verifikasi ulang ke LKPP dari frontend
      // Token LKPP sudah diverifikasi langsung oleh ssoController.js di backend
      // saat SSO push masuk (token masih fresh). JWT ini hanya dibuat jika LKPP approve.

      // 3. Simpan Token ke LocalStorage
      localStorage.setItem('dietalase_session_token', nonceToken);

      // 4. Update State User
      setUser({ 
        id: decodedUserData.userId || decodedUserData.id, 
        username: decodedUserData.username, 
        realName: decodedUserData.realName, 
        role: decodedUserData.role,
        email: decodedUserData.email,
        namaInstansi: decodedUserData.namaInstansi,
        namaSatker: decodedUserData.namaSatker,
        lkppToken: tokenLKPPAsli
      });
      
      setIsAuthenticated(true);
      return { success: true };

    } catch (err) {
      console.error("Proses autentikasi digagalkan:", err);
      setError("Gagal memproses token login: " + err.message);
      logout();
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
};

  /**
   * Logout pengguna.
   */
  const logout = () => {
    localStorage.removeItem('dietalase_session_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Cek sesi saat aplikasi pertama kali dimuat
  useEffect(() => {
    const validateSession = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const nonceFromUrl = searchParams.get('nonce') || searchParams.get('token');

      if (nonceFromUrl) {
        console.log("[AuthContext] Token ditemukan di URL, memulai login SSO...");
        await exchangeToken(nonceFromUrl);
        
        // Membersihkan URL agar rapi dan tidak menyisakan token panjang di Address Bar
        const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
        window.history.replaceState({path: newUrl}, '', newUrl);
      } 
      else {
        // Cek LocalStorage jika user me-refresh halaman
        const token = localStorage.getItem('dietalase_session_token');
        if (token) {
          try {
            const decoded = JSON.parse(atob(token.split('.')[1]));
            
            if (decoded.exp * 1000 < Date.now()) {
              throw new Error("Sesi kedaluwarsa");
            }

            setUser({ 
              id: decoded.userId || decoded.id, 
              username: decoded.username, 
              realName: decoded.realName, 
              role: decoded.role,
              email: decoded.email,
              namaInstansi: decoded.namaInstansi,
              namaSatker: decoded.namaSatker,
              lkppToken: decoded.lkppToken
            });
            setIsAuthenticated(true);
          } catch (err) {
            console.error("Validasi sesi gagal:", err);
            logout(); 
          }
        }
      }
      setLoading(false);
    };

    validateSession();
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    exchangeToken,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;